import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedSection extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    const sectionId = this.dataset.sectionId;
    const currentProductId = this.dataset.currentProductId;

    if (!sectionId) return;

    let productIds = RecentlyViewed.getProducts();

    // On PDP, exclude the product being viewed
    if (currentProductId) {
      productIds = productIds.filter((id) => id !== currentProductId);
    }

    if (productIds.length === 0) {
      this.#hide();
      return;
    }

    const query = productIds.map((id) => `id:${id}`).join(' OR ');
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('sections', sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        this.#hide();
        return;
      }

      const json = await response.json();
      const sectionHtml = json[sectionId];

      if (!sectionHtml) {
        this.#hide();
        return;
      }

      // Parse the fetched section HTML and extract our element's inner content
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const renderedSection = doc.querySelector(`recently-viewed-section[id="${this.id}"]`);

      if (renderedSection?.innerHTML?.trim()) {
        this.innerHTML = renderedSection.innerHTML;
      } else {
        this.#hide();
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        this.#hide();
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  #hide() {
    this.style.display = 'none';
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
